import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink, Upload } from "lucide-react";
import { api, formatError } from "../../lib/api";

const EMPTY = { title: "", url: "", image_url: "", description: "" };

export default function ImportantLinksManagement() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try { setLinks((await api.get("/important-links")).data); }
    catch { toast.error("Failed to load links"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (l) => { setEditing(l); setForm({ title: l.title, url: l.url, image_url: l.image_url || "", description: l.description || "" }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = async e => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and URL are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/important-links/${editing.id}`, form);
        toast.success("Link updated");
      } else {
        await api.post("/important-links", form);
        toast.success("Link created");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await api.delete(`/important-links/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const onImageUpload = async (e, linkId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/important-links/${linkId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      setLinks(ls => ls.map(l => l.id === linkId ? { ...l, image_url: res.data.image_url } : l));
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Important Links</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the public-facing links directory.</p>
        </div>
        <button onClick={openCreate} className="tl-btn inline-flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Add Link
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : links.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ExternalLink className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No links yet. Click "Add Link" to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {links.map(l => (
            <div key={l.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-[16/9] bg-gray-50 flex items-center justify-center relative group">
                {l.image_url ? (
                  <img src={l.image_url} alt={l.title} className="w-full h-full object-contain p-3" />
                ) : (
                  <ExternalLink className="w-10 h-10 text-gray-300" />
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <span className="text-white text-xs font-semibold flex items-center gap-1">
                    {uploading ? "Uploading…" : <><Upload className="w-4 h-4" /> Upload image</>}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => onImageUpload(e, l.id)} disabled={uploading} />
                </label>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm text-[#1A1A1A] mb-1">{l.title}</h3>
                {l.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{l.description}</p>}
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#DC2A1C] hover:underline truncate block">{l.url}</a>
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button onClick={() => openEdit(l)} className="flex items-center gap-1 text-xs text-gray-600 hover:text-[#DC2A1C] transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => onDelete(l.id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{editing ? "Edit Link" : "New Link"}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={onSave} className="space-y-4">
              {[
                { label: "Title *", name: "title", placeholder: "e.g. DGFT Portal" },
                { label: "URL *", name: "url", placeholder: "https://…" },
                { label: "Image URL (optional)", name: "image_url", placeholder: "https://…/logo.png" },
                { label: "Description (optional)", name: "description", placeholder: "Brief description" },
              ].map(({ label, name, placeholder }) => (
                <div key={name}>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-gray-500 mb-1.5">{label}</label>
                  <input name={name} value={form[name]} onChange={onChange} placeholder={placeholder}
                    className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#DC2A1C]" />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="tl-btn-outline flex-1 text-sm">Cancel</button>
                <button type="submit" disabled={saving} className="tl-btn flex-1 text-sm disabled:opacity-60">
                  {saving ? "Saving…" : editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
